/**
 * Suggerimenti mentre si scrive nel campo di ricerca sempre visibile (snippets/gf-search-field.liquid).
 * Usa la ricerca predittiva di Shopify (/search/suggest.json): collezioni e prodotti con foto, brand,
 * nome breve, colore e prezzo, più «Vedi tutti i risultati». Il modulo resta un normale modulo: invio =
 * pagina dei risultati, anche senza JavaScript.
 * Tastiera: freccia giù entra nei suggerimenti, frecce su/giù si muovono, Esc chiude e torna al campo.
 */
const MIN_CHARS = 2;
const DELAY = 220;

/** prezzo nel formato del negozio (Impostazioni › Generali), es. «€{{amount_with_comma_separator}}» */
function formatMoney(amount, format) {
  const group = (number, decimals, thousands, decimal) => {
    const [int, dec] = number.toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
  };
  return String(format).replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(amount, 0, ',', '.');
      case 'amount_with_comma_separator':
        return group(amount, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(amount, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return group(amount, 2, "'", '.');
      case 'amount_with_space_separator':
        return group(amount, 2, ' ', ',');
      default:
        return group(amount, 2, ',', '.');
    }
  });
}

/** stesso nome breve del tema (snippets/gf-short-title.liquid): via categoria e codici articolo finali */
function shortTitle(title, vendor) {
  const parts = String(title).split(' - ');
  const name = (parts.length >= 2 ? parts[1] : parts[0]).trim();
  const words = name.split(' ');
  const isCode = (w) => {
    if (!/\d/.test(w)) return false;
    if (w.includes('-')) return w.length >= 10;
    if (/^\d+$/.test(w)) return w.length >= 5;
    return w.length >= 6;
  };
  while (words.length && isCode(words[words.length - 1])) words.pop();
  let clean = words.join(' ').trim();
  if (!clean) clean = parts[0].replace(vendor || '', '').trim();
  return { name: clean || title, color: parts.length >= 3 ? parts[parts.length - 1].trim() : '' };
}

const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

class GfSearch extends HTMLElement {
  #abort = null;
  #request = null;
  #timer = 0;
  #input = null;
  #panel = null;
  #status = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.#input = this.querySelector('input[type=search]');
    this.#panel = this.querySelector('[data-gf-suggest]');
    this.#status = this.querySelector('[data-gf-suggest-status]');
    if (!this.#input || !this.#panel) return;

    this.#input.addEventListener('input', () => this.#schedule(), { signal });
    this.#input.addEventListener('focus', () => this.#input.value.trim().length >= MIN_CHARS && this.#schedule(), { signal });
    this.#input.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowDown' && !this.#panel.hidden) {
          event.preventDefault();
          this.#links()[0]?.focus();
        } else if (event.key === 'Escape' && !this.#panel.hidden) {
          event.stopPropagation();
          this.#close();
        }
      },
      { signal }
    );
    this.#panel.addEventListener(
      'keydown',
      (event) => {
        const links = this.#links();
        const i = links.indexOf(document.activeElement);
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          links[Math.min(i + 1, links.length - 1)]?.focus();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          if (i <= 0) this.#input.focus();
          else links[i - 1].focus();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.#close();
          this.#input.focus();
        }
      },
      { signal }
    );
    // chiude toccando o cliccando fuori, o quando il fuoco esce dal componente con la tastiera.
    // relatedTarget null = clic su un link che non prende il fuoco (Safari, Firefox su Mac): lo gestisce pointerdown,
    // altrimenti la tendina si svuoterebbe prima che il clic arrivi al link
    document.addEventListener('pointerdown', (event) => !this.contains(event.target) && this.#close(), { signal });
    this.addEventListener('focusout', (event) => event.relatedTarget && !this.contains(event.relatedTarget) && this.#close(), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#request?.abort();
    clearTimeout(this.#timer);
  }

  #links() {
    return [...this.#panel.querySelectorAll('a')];
  }

  #schedule() {
    clearTimeout(this.#timer);
    const terms = this.#input.value.trim();
    if (terms.length < MIN_CHARS) {
      this.#close();
      return;
    }
    this.#timer = setTimeout(() => this.#fetch(terms), DELAY);
  }

  async #fetch(terms) {
    this.#request?.abort();
    this.#request = new AbortController();
    const url = new URL(this.dataset.suggestUrl || '/search/suggest', location.origin);
    url.pathname = url.pathname.replace(/\/?$/, '.json');
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    try {
      const response = await fetch(url, { signal: this.#request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      if (this.#input.value.trim() !== terms) return;
      this.#render(terms, data?.resources?.results || {});
    } catch (error) {
      if (error.name !== 'AbortError') this.#close();
    }
  }

  #price(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    const currency = window.Shopify?.currency?.active || 'EUR';
    // valuta del negozio: stesso formato delle schede («€210,00»), non quello del browser («210,00 €»)
    const { moneyFormat, shopCurrency } = this.dataset;
    if (moneyFormat && currency === shopCurrency) return formatMoney(amount, moneyFormat);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  #render(terms, results) {
    const products = (results.products || []).slice(0, 6);
    const collections = (results.collections || []).slice(0, 3);
    const t = this.dataset;
    const allUrl = `${this.querySelector('form')?.getAttribute('action') || '/search'}?q=${encodeURIComponent(terms)}&options%5Bprefix%5D=last&type=product`;
    let html = '';
    if (collections.length) {
      html += `<p class="gf-suggest__label">${escapeHtml(t.labelCollections)}</p><ul class="gf-suggest__collections" role="list">`;
      html += collections.map((c) => `<li><a href="${escapeHtml(c.url)}">${escapeHtml(c.title)}</a></li>`).join('');
      html += '</ul>';
    }
    if (products.length) {
      html += `<p class="gf-suggest__label">${escapeHtml(t.labelProducts)}</p><ul class="gf-suggest__products" role="list">`;
      html += products
        .map((p) => {
          const { name, color } = shortTitle(p.title, p.vendor);
          const img = p.featured_image?.url || p.image || '';
          const src = img ? `${img}${img.includes('?') ? '&' : '?'}width=120` : '';
          return `<li><a href="${escapeHtml(p.url)}" aria-label="${escapeHtml([p.title, this.#price(p.price)].filter(Boolean).join(', '))}">
            <span class="gf-suggest__media">${src ? `<img src="${escapeHtml(src)}" alt="" width="60" height="75" loading="lazy">` : ''}</span>
            <span class="gf-suggest__text" aria-hidden="true">
              <span class="gf-suggest__brand">${escapeHtml(p.vendor)}</span>
              <span class="gf-suggest__name">${escapeHtml(name)}</span>
              ${color ? `<span class="gf-suggest__color">${escapeHtml(color)}</span>` : ''}
            </span>
            <span class="gf-suggest__price" aria-hidden="true">${escapeHtml(this.#price(p.price))}</span>
          </a></li>`;
        })
        .join('');
      html += '</ul>';
    }
    if (!products.length && !collections.length) {
      html += `<p class="gf-suggest__empty">${escapeHtml(t.labelEmpty)}</p>`;
    } else {
      html += `<a class="gf-suggest__all" href="${escapeHtml(allUrl)}">${escapeHtml((t.labelAll || '').replace('[terms]', () => terms))}</a>`;
    }
    this.#panel.innerHTML = html;
    this.#panel.hidden = false;
    if (this.#status) {
      this.#status.textContent = (products.length + collections.length ? t.labelCount || '' : t.labelEmpty || '').replace('[count]', String(products.length + collections.length));
    }
  }

  #close() {
    // ferma anche la richiesta in arrivo, così la tendina non si riapre da sola dopo Esc o un tocco fuori
    clearTimeout(this.#timer);
    this.#request?.abort();
    if (this.#status) this.#status.textContent = '';
    if (!this.#panel || this.#panel.hidden) return;
    this.#panel.hidden = true;
    this.#panel.innerHTML = '';
  }
}

if (!customElements.get('gf-search')) customElements.define('gf-search', GfSearch);

/**
 * Telefono: l'icona della ricerca apre e chiude la riga sotto il logo (#GfSearchRow) e porta il cursore nel campo.
 * Si chiude anche con Esc (a tendina chiusa) o toccando fuori dall'intestazione quando il campo è vuoto.
 */
function searchRow() {
  return document.getElementById('GfSearchRow');
}

function setRowOpen(open, { focus = true } = {}) {
  const row = searchRow();
  if (!row) return;
  row.toggleAttribute('data-open', open);
  const buttons = document.querySelectorAll('[data-gf-search-open]');
  buttons.forEach((button) => button.setAttribute('aria-expanded', String(open)));
  if (open && focus) row.querySelector('input[type=search]')?.focus();
  if (!open && focus) [...buttons].find((button) => button.offsetParent)?.focus();
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-gf-search-open]');
  if (!button) return;
  setRowOpen(!searchRow()?.hasAttribute('data-open'));
});

document.addEventListener('keydown', (event) => {
  const row = searchRow();
  if (event.key !== 'Escape' || !row?.hasAttribute('data-open') || !row.contains(document.activeElement)) return;
  // la prima Esc chiude i suggerimenti (gestita da gf-search, che ferma l'evento); la seconda chiude la riga
  setRowOpen(false);
});

document.addEventListener('pointerdown', (event) => {
  const row = searchRow();
  if (!row?.hasAttribute('data-open') || event.target.closest('#header-component')) return;
  if (!row.querySelector('input[type=search]')?.value.trim()) setRowOpen(false, { focus: false });
});
